import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from './UIComponents';
import { encodePostAuthIntentForQuery, savePostAuthIntent } from '../../utils/postAuthIntent';

interface LoginPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  actionType?: string;
  returnTo?: {
    pathname: string;
    search?: string;
    hash?: string;
  } | null;
}

const LoginPromptModal: React.FC<LoginPromptModalProps> = ({
  isOpen,
  onClose,
  actionType = 'wykonać tę akcję',
  returnTo = null,
}) => {
  const normalizedReturnToPath = useMemo(() => {
    if (!returnTo?.pathname) {
      return null;
    }
    return `${returnTo.pathname}${returnTo.search || ''}${returnTo.hash || ''}`;
  }, [returnTo]);

  useEffect(() => {
    if (!isOpen || !normalizedReturnToPath) {
      return;
    }
    savePostAuthIntent(normalizedReturnToPath, 'client-gate-modal');
  }, [isOpen, normalizedReturnToPath]);

  const encodedIntent = normalizedReturnToPath
    ? encodePostAuthIntentForQuery(normalizedReturnToPath)
    : null;
  const loginTarget = encodedIntent ? `/login?postAuthIntent=${encodedIntent}` : '/login';
  const registerClientTarget = encodedIntent
    ? `/register/client?postAuthIntent=${encodedIntent}`
    : '/register/client';

  const authIntentState = {
    from: returnTo || undefined,
    authIntent: 'client-action',
    registerPath: '/register/client',
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 md:flex md:items-center md:justify-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Wymagane logowanie klienta"
    >
      <div
        className="h-full w-full overflow-y-auto bg-primary-dark-200 p-6 pb-8 md:h-auto md:max-w-md md:rounded-xl md:border md:border-primary-dark-300/70 md:shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-xl font-bold text-primary-light-100">Aby kontynuować jako klient</h3>
          <button
            onClick={onClose}
            className="text-primary-light-400 hover:text-primary-light-200 transition-colors"
            aria-label="Zamknij"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <p className="mb-6 text-primary-light-300">
          Aby {actionType}, zaloguj się na konto klienta albo załóż nowe konto klienta.
        </p>

        <div className="flex flex-col gap-3">
          <Link to={loginTarget} state={authIntentState} className="w-full" onClick={onClose}>
            <Button variant="primary" className="w-full">
              Zaloguj się
            </Button>
          </Link>

          <Link
            to={registerClientTarget}
            state={authIntentState}
            className="w-full"
            onClick={onClose}
          >
            <Button variant="neutral" className="w-full">
              Załóż konto klienta
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPromptModal;
